/**
 * Billable-time rounding, shared by every surface that shows a user what their
 * typed duration will actually bill as.
 *
 * Mirrors `round_minutes` in `app/services/billing_rate_service.py` — always
 * rounds *up*, because a minute of work bills as a tenth of an hour. The
 * increment is a firm-wide admin setting; 1 (or 0) means no rounding.
 */

export function roundUpToIncrement(minutes: number, increment: number): number {
  if (minutes <= 0) return 0;
  if (increment <= 1) return minutes;
  return Math.ceil(minutes / increment) * increment;
}

export function formatMinutesAsHm(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = Math.round((minutes - hours * 60) * 10) / 10;
  const restLabel = Number.isInteger(rest) ? String(rest) : rest.toFixed(1);
  if (hours > 0) return rest > 0 ? `${hours}h ${restLabel}m` : `${hours}h`;
  return `${restLabel}m`;
}

/** Trim a pointless trailing zero: 3.30 reads better as 3.3, 3.33 stays put. */
function formatHours(hours: number): string {
  const fixed = hours.toFixed(2);
  return fixed.endsWith('0') ? fixed.slice(0, -1) : fixed;
}

/**
 * The line shown under a duration field: what was typed, and what it bills as.
 * e.g. "3h 20m · bills as 3.3 hr"
 */
export function billableSummary(minutes: number, increment: number): string {
  const billed = roundUpToIncrement(minutes, increment);
  return `${formatMinutesAsHm(minutes)} · bills as ${formatHours(billed / 60)} hr`;
}
